import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import moment from 'moment';

import * as parentPasswordModalActions from '@/components/modals/parentPasswordModal/Actions';
import * as videoModalActions from '@/components/modals/videoModal/Actions';
import * as learningPathActions from '@/pages/learningPath/Actions';
import * as sharedActions from '@/shared/Actions';
import { LEARNING_PATH_ONBOARDING_VIMEO_ID } from '@/pages/learningPath/Constants';
import {
	APP_ENVIRONMENT,
	COOKIES,
	FRONTEND_ROUTES,
	WRAPPED_FEATURE_ENABLED,
} from '@/services/Constants';
import { EVENTS_TYPES, PLAY_MODE_TYPES } from '@/shared/Constants';
import { getCookie } from '@/services/Helper';
import { BRIDGE_GRADE_ID } from '@/features/bridge';
import { WRAPPED_START_DATE } from '@/features/wrapped';
import { historyPush } from '@/services/History';

// Keys supported by the navigation hook
export type NavigationKey =
	| 'learning-path'
	| 'world'
	| 'bridge'
	| 'year-summary'
	| 'goal-settings'
	| 'activity'
	| 'video-modal'
	| 'feedback'
	| 'settings'
	| 'logout';

export interface NavigationItem {
	key: NavigationKey;
	title: string;
	icon: string;
	path?: string;
	disabled?: boolean;
}

export interface UseNavigationResult {
	handleClickNavigation: (item: NavigationItem) => void;
	navigationOptions: NavigationItem[];
	activeOption: NavigationItem | undefined;
}

const useNavigation = (): UseNavigationResult => {
	const { t } = useTranslation();
	const location = useLocation();
	const dispatch = useDispatch();

	const userData = useSelector((state: any) => state.sharedReducer.userData);
	const finishedDailyGoal = useSelector((state: any) =>
		Boolean(state.learningPathReducer.finishedDailyGoal),
	);

	const isParentVerifiedCookie = getCookie(COOKIES.parentPasswordVerified);
	const isParentVerifiedFromStore = useSelector((state: any) =>
		Boolean(state.parentPasswordModalReducer.isParentVerified),
	);
	const isParentVerified = Boolean(isParentVerifiedCookie) || isParentVerifiedFromStore;

	const passwordSettings: Record<string, boolean> =
		useSelector((state: any) => state.sharedReducer.userData?.settings?.password) || {};

	const { play_mode, sidk_user, is_basispoort } = userData || {};

	const options: NavigationItem[] = [
		{
			key: 'learning-path',
			title:
				play_mode === PLAY_MODE_TYPES.FREE_PLAY
					? t('sidebar.world')
					: t('sidebar.learning_path'),
			icon: play_mode === PLAY_MODE_TYPES.FREE_PLAY ? 'icon-map' : 'icon-route',
			path:
				play_mode === PLAY_MODE_TYPES.FREE_PLAY
					? FRONTEND_ROUTES.FREE_PLAY
					: FRONTEND_ROUTES.HOME,
		},
	];

	if (userData?.grade_id === BRIDGE_GRADE_ID) {
		options.push({
			key: 'bridge',
			title: t('sidebar.bridge'),
			icon: 'icon-flashlight-torch',
			path: FRONTEND_ROUTES.BRIDGE,
		});
	}

	if (play_mode === 'free_play_after_daily_goal') {
		options.push({
			key: 'world',
			title: t('sidebar.world'),
			icon: 'icon-map',
			path: FRONTEND_ROUTES.FREE_PLAY,
			disabled: !finishedDailyGoal,
		});
	}

	const isProductionEnvironment = APP_ENVIRONMENT === 'production';
	const wrappedStartDatePassed = moment().isSameOrAfter(moment(WRAPPED_START_DATE), 'day');
	const showWrappedMenuItem =
		WRAPPED_FEATURE_ENABLED && (!isProductionEnvironment || wrappedStartDatePassed);

	if (showWrappedMenuItem) {
		options.push({
			key: 'year-summary',
			title: t('sidebar.year_summary'),
			icon: 'icon-union',
			path: FRONTEND_ROUTES.WRAPPED,
		});
	}

	if (!sidk_user) {
		options.push({
			key: 'goal-settings',
			title: t('sidebar.goal_settings'),
			icon: 'icon-tool',
			path: FRONTEND_ROUTES.GOAL_SETTINGS,
		});
	}

	options.push({
		key: 'activity',
		title: t('sidebar.activity_and_progress'),
		icon: 'icon-book-open',
		path: FRONTEND_ROUTES.ACTIVITY,
	});

	options.push({
		key: 'video-modal',
		title: t('sidebar.video_modal'),
		icon: 'icon-play',
	});

	if (!sidk_user) {
		options.push({
			key: 'feedback',
			title: t('sidebar.give_feedback'),
			icon: 'icon-report-annotation',
		});
		options.push({
			key: 'settings',
			title: t('sidebar.settings'),
			icon: 'icon-settings',
			path: FRONTEND_ROUTES.SETTINGS,
		});
	}

	options.push({
		key: 'logout',
		title: is_basispoort ? t('sidebar.logout_basispoort') : t('sidebar.logout'),
		icon: 'icon-logout',
	});

	const handleClickNavigation = (item: NavigationItem) => {
		if (item.key === 'goal-settings') {
			dispatch(
				sharedActions.sendEventRequest({
					event_name: EVENTS_TYPES.LEARNING_PATH_ADJUST_BUTTON,
				}),
			);
			if (passwordSettings.update_learning_goals && !isParentVerified) {
				dispatch(parentPasswordModalActions.openParentPasswordModal(item.path || ''));
			} else if (item.path) {
				historyPush(item.path);
			}
		} else if (['learning-path', 'world', 'bridge', 'year-summary'].includes(item.key)) {
			if (item.path) historyPush(item.path);
		} else if (item.key === 'activity') {
			if (passwordSettings.view_activity_page && !isParentVerified) {
				dispatch(parentPasswordModalActions.openParentPasswordModal(item.path || ''));
			} else if (item.path) {
				historyPush(item.path);
			}
		} else if (item.key === 'feedback') {
			dispatch(sharedActions.toggleGeneralFeedbackForm(true));
		} else if (item.key === 'logout') {
			dispatch(sharedActions.logout());
		} else if (item.key === 'video-modal') {
			dispatch(
				videoModalActions.toggleVideoModal({
					open: true,
					videoId: LEARNING_PATH_ONBOARDING_VIMEO_ID,
					onClose: () => {
						dispatch(
							learningPathActions.updateOnboardingDataRequest(
								'onboarding_learning_path_at',
							),
						);
					},
				}),
			);
		} else if (item.key === 'settings') {
			if (passwordSettings.view_settings_page && !isParentVerifiedFromStore) {
				dispatch(parentPasswordModalActions.openParentPasswordModal(item.path || ''));
			} else if (item.path) {
				historyPush(item.path);
			}
		}
	};

	const activeOption = options.find(
		(option) => option.path && location.pathname.includes(option.path!),
	);

	return {
		handleClickNavigation,
		navigationOptions: options,
		activeOption,
	};
};

export default useNavigation;
